import {
  faArrowsDownToLine,
  faBell,
  faBook,
  faBookOpen,
  faBug,
  faEllipsisVertical,
  faExternalLinkAlt,
  faFileCsv,
  faFileDownload,
  faInfoCircle,
  faNetworkWired
} from "@fortawesome/free-solid-svg-icons";
import { RefObject, useCallback, useRef, useState } from "react";
import { Col, Overlay, Popover } from "react-bootstrap";
import { isDesktop } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { endpoints, getApiEndpoint, getCodeSystemEndpoint } from "utils";

import Button from "../Button";
import { OptionButton } from "./common";
import optionsData from "./utils/optionsData";

const getOptionsData = (path: string) => {
  if (path.includes("icd10")) return optionsData.icd10;
  if (path.includes("icpc2")) return optionsData.icpc2;
  if (path.includes("nkpk")) return optionsData.nkpk;
  if (path.includes("phbu")) return optionsData.phbu;
  if (path.includes("norpat")) return optionsData.norpat;
  if (path.includes("anatomisk")) return optionsData.anatomisk;
  if (path.includes("provemateriale")) return optionsData.provemateriale;
  if (path.includes("tekstligeresultatverdier"))
    return optionsData.tekstligeResultatverdier;
  if (path.includes("nlk")) return optionsData.nlk;
  if (path.includes("undersokelsesmetode"))
    return optionsData.undersokelsesmetode;
  if (path.includes("adm")) return optionsData.adm;
  if (path.includes("snomed")) return optionsData.snomedct;
  return null;
};

const Options: React.FC = () => {
  const { t } = useTranslation();
  const [showPopover, setShowPopover] = useState<boolean>(false);
  const { pathname } = useLocation();
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const popoverRef = useRef<HTMLElement>(null);

  const handlePopoverClick = useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >(
    (event) => {
      setShowPopover(!showPopover);
      if (event.target) {
        setTarget(event.target as HTMLElement);
      }
    },
    [showPopover]
  );

  const handleOptionsData = (property: string) => {
    const data = getOptionsData(pathname);
    return property ? data?.[property] : data;
  };

  return (
    <Col xs="auto" ref={popoverRef} className="d-flex gap-2">
      <Button
        size="md"
        icon={faEllipsisVertical}
        iconright
        onClick={handlePopoverClick}
        className="p-0 pe-0"
        ariaLabel={t("codesystems.options.showMoreOptions")}
      >
        {isDesktop &&
          window.innerWidth > 700 &&
          t("codesystems.options.showOptions")}
      </Button>
      <Overlay
        show={showPopover}
        target={target}
        placement="bottom-end"
        // eslint-disable-next-line react-hooks/refs
        container={popoverRef.current?.parentNode as HTMLElement}
        containerPadding={20}
        rootClose
        rootCloseEvent="click"
        onHide={() => setShowPopover(false)}
      >
        <Popover
          id="popover-contained"
          ref={popoverRef as RefObject<HTMLDivElement>}
          className="p-0"
        >
          <Popover.Body className="px-1">
            {handleOptionsData("codingGuide") && (
              <OptionButton
                icon={faBookOpen}
                text={handleOptionsData("codingGuide")}
                onClick={() => {
                  const url = handleOptionsData("codingGuideLink");
                  if (typeof url === "string") {
                    window.open(url, "_blank");
                  }
                }}
              />
            )}

            {handleOptionsData("codingGuideNorpat") && (
              <OptionButton
                icon={faBookOpen}
                text={handleOptionsData("codingGuideNorpat")}
                onClick={() => {
                  const url = handleOptionsData("codingGuideNorpatLink");
                  if (typeof url === "string") {
                    window.open(url, "_blank");
                  }
                }}
              />
            )}

            {handleOptionsData("codingGuideApat") && (
              <OptionButton
                icon={faBookOpen}
                text={handleOptionsData("codingGuideApat")}
                onClick={() => {
                  const url = handleOptionsData("codingGuideApatLink");
                  if (typeof url === "string") {
                    window.open(url, "_blank");
                  }
                }}
              />
            )}

            {handleOptionsData("orphanet") && (
              <OptionButton
                icon={faExternalLinkAlt}
                text={handleOptionsData("orphanet")}
                onClick={() => {
                  const url = handleOptionsData("orphanetLink");
                  if (typeof url === "string") {
                    window.open(url, "_blank");
                  }
                }}
              />
            )}

            {handleOptionsData("about") && (
              <OptionButton
                icon={faInfoCircle}
                text={handleOptionsData("about")}
                onClick={() => {
                  const url = handleOptionsData("aboutLink");
                  if (typeof url === "string") {
                    window.open(url, "_blank");
                  }
                }}
              />
            )}

            {handleOptionsData("historicalFiles") && (
              <OptionButton
                icon={faBook}
                text={handleOptionsData("historicalFiles")}
                onClick={() => {
                  const url = handleOptionsData("historicalFilesLink");
                  if (typeof url === "string") {
                    window.open(url, "_blank");
                  }
                }}
              />
            )}

            {handleOptionsData("subsrcibe") && (
              <OptionButton
                icon={faBell}
                text={t("codesystems.options.subscribe")}
                onClick={() => {
                  const url = handleOptionsData("subsrcibe");
                  if (typeof url === "string") {
                    window.open(url, "_blank");
                  }
                }}
              />
            )}

            {handleOptionsData("downloadExcel") && (
              <OptionButton
                icon={faFileDownload}
                text={t("codesystems.options.downloadExcel")}
                onClick={() => {
                  const url = handleOptionsData("downloadExcel");
                  if (typeof url === "string") {
                    window.open(url, "_blank");
                  }
                }}
              />
            )}

            {handleOptionsData("hasExcelApiEndpoint") && (
              <OptionButton
                icon={faFileDownload}
                text={t("codesystems.options.downloadExcel")}
                onClick={() => {
                  const url = getApiEndpoint(
                    getCodeSystemEndpoint(pathname as string) as string,
                    "",
                    {
                      download: true,
                      fileType: "EXCEL"
                    }
                  );
                  if (typeof url === "string") {
                    window.open(url, "_blank");
                  }
                }}
              />
            )}

            {handleOptionsData("downloadCsv") && (
              <OptionButton
                icon={faFileCsv}
                text={t("codesystems.options.downloadCsv")}
                onClick={() => {
                  const url = handleOptionsData("downloadCsv");
                  if (typeof url === "string") {
                    window.open(url, "_blank");
                  }
                }}
              />
            )}

            {handleOptionsData("downloadXML") && (
              <OptionButton
                icon={faArrowsDownToLine}
                text={t("codesystems.options.downloadXML")}
                onClick={() => {
                  const url = handleOptionsData("downloadXML");
                  if (typeof url === "string") {
                    window.open(url, "_blank");
                  }
                }}
              />
            )}

            {handleOptionsData("api") && (
              <OptionButton
                icon={faNetworkWired}
                text={t("codesystems.options.getApi")}
                onClick={() => window.open(endpoints.apiUrlDev, "_blank")}
              />
            )}

            {handleOptionsData("report") && (
              <OptionButton
                icon={faBug}
                text={t("codesystems.options.reportError")}
                onClick={() => {
                  const url = handleOptionsData("report");
                  if (typeof url === "string") {
                    window.open(url, "_blank");
                  }
                }}
                hasExtraText
                extraText={handleOptionsData("reportEmail") as string}
              />
            )}
          </Popover.Body>
        </Popover>
      </Overlay>
    </Col>
  );
};

export default Options;
